import { z } from "zod";

export const ukPostcodeRegex = /^([A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2})$/i;

export function isUnavailableBookingDate(date: Date) {
  const day = date.getDay();
  return day === 0 || day === 6;
}

export function getEarliestBookingDate(today = new Date()) {
  const candidate = new Date(today);
  candidate.setDate(candidate.getDate() + 1);
  candidate.setHours(0, 0, 0, 0);

  while (isUnavailableBookingDate(candidate)) {
    candidate.setDate(candidate.getDate() + 1);
  }

  return candidate;
}

export function formatDateInput(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function isValidBookingDate(value: string) {
  if (!value) return false;
  const selected = new Date(`${value}T00:00:00`);
  const earliest = getEarliestBookingDate();
  return selected >= earliest && !isUnavailableBookingDate(selected);
}

export const postcodeSchema = z.object({
  postcode: z.string().trim().min(1, "Postcode is required.").regex(ukPostcodeRegex, "Enter a valid UK postcode."),
});

function requiredEnum<const T extends [string, ...string[]]>(values: T, message: string) {
  return z.preprocess((value) => (value === "" ? undefined : value), z.enum(values, { required_error: message, invalid_type_error: message }));
}

export const serviceSchema = z.object({
  service: requiredEnum(["skip", "man-van", "grab"], "Choose a service."),
});

export const skipSizeSchema = z.object({
  skipSize: requiredEnum(["4-yard", "6-yard", "8-yard", "10-yard", "12-yard"], "Choose a skip size."),
});

export const loadSizeSchema = z.object({
  loadSize: requiredEnum(["small", "medium", "large", "full-van"], "Choose a load size."),
});

export const materialTypeSchema = z.object({
  materialType: requiredEnum(["soil", "hardcore", "mixed-inert", "green-waste"], "Choose a material type."),
});

export const bookingDetailsSchema = z
  .object({
    date: z.string().min(1, "Booking date is required.").refine(isValidBookingDate, "Choose an available date from the picker."),
    timeSlot: z.string().min(1, "Time slot is required."),
    fullName: z.string().trim().min(1, "Full name is required."),
    email: z.string().trim().email("Enter a valid email address."),
    phone: z.string().trim().min(1, "Phone number is required."),
    addressLine1: z.string().trim().min(1, "Address line 1 is required."),
    addressLine2: z.string().optional(),
    townCity: z.string().trim().min(1, "Town/city is required."),
    county: z.string().optional(),
    postcode: z.string().trim().min(1, "Postcode is required.").regex(ukPostcodeRegex, "Enter a valid UK postcode."),
    isCompanyBooking: z.boolean(),
    companyName: z.string().optional(),
    driverNotes: z.string().optional(),
  })
  .superRefine((value, context) => {
    if (value.isCompanyBooking && !value.companyName?.trim()) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Company name is required for company bookings.",
        path: ["companyName"],
      });
    }
  });
